/** The ONLY place fetch is called. A component that calls fetch directly is wrong.
 *
 * The access token lives in a module variable - in memory only. Not localStorage, not
 * sessionStorage, not a readable cookie: an XSS that can read your token owns the account for
 * as long as the token lives. The refresh token is an httpOnly cookie the JavaScript never
 * sees; all this module does is POST to /auth/refresh with credentials included. */
import type { Session } from "../types/api.js";

/** Where the API is, from `VITE_API_BASE_URL` in the frontend `.env`.
 *
 * The default is RELATIVE on purpose, and the default is what should normally be used: a
 * relative base means the API is whatever origin served this page, which is what keeps the
 * `SameSite=Strict` refresh cookie working and satisfies `connect-src 'self'` in the CSP. In
 * development the Vite proxy forwards `/api` to the backend port so that stays true there too.
 *
 * Setting an absolute URL here is supported but has consequences the value alone cannot carry -
 * CORS on the backend and a weaker cookie policy. frontend/.env.example spells them out. */
/** Exported because sign-in and sign-out deliberately bypass the shared client (see
 * auth.api.ts) and would otherwise hard-code a path. A hard-coded `/api/v1/auth/login`
 * is correct only while the console and the API share an origin: point
 * VITE_API_BASE_URL at a separate API host and those two calls alone keep going to the
 * console host, where they 404, so sign-in fails while every other screen works. */
export const BASE = import.meta.env.VITE_API_BASE_URL?.trim() || "/api/v1";

let accessToken: string | null = null;
let onSessionLost: ((reason: string) => void) | null = null;

/** Concurrent 401s must share ONE refresh. Five parallel queries firing five refreshes would
 * have the second rotation invalidate the first, and the user would be thrown into a logout
 * loop. Every caller awaits this same promise. */
let inFlightRefresh: Promise<Session | null> | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export function onSessionEnded(handler: (reason: string) => void): void {
  onSessionLost = handler;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
    readonly correlationId: string | null,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

interface ErrorBody {
  error?: { code?: string; message?: string; correlationId?: string; details?: unknown };
}

async function toApiError(res: Response): Promise<ApiError> {
  let body: ErrorBody = {};
  try {
    body = (await res.json()) as ErrorBody;
  } catch {
    // A non-JSON error body (a proxy error page, say) still has to become a sentence.
  }
  return new ApiError(
    body.error?.message ?? `the request failed (HTTP ${res.status})`,
    res.status,
    body.error?.code ?? "UNKNOWN",
    body.error?.correlationId ?? res.headers.get("X-Correlation-ID"),
    body.error?.details,
  );
}

function authHeaders(): HeadersInit {
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {};
}

/** Exchanges the httpOnly refresh cookie for a new access token. Returns null when there is no
 * usable session. */
export function refreshSession(): Promise<Session | null> {
  inFlightRefresh ??= (async () => {
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        accessToken = null;
        return null;
      }
      const session = (await res.json()) as Session;
      accessToken = session.accessToken;
      return session;
    } catch {
      accessToken = null;
      return null;
    } finally {
      // Cleared whatever happened, so a later 401 starts a fresh attempt rather than awaiting
      // a promise that already settled.
      inFlightRefresh = null;
    }
  })();
  return inFlightRefresh;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  /** Internal: prevents a refresh loop. */
  retrying?: boolean;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, retrying = false } = options;

  const res = await fetch(`${BASE}${path}`, {
    method,
    credentials: "include",
    headers: {
      ...authHeaders(),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });

  if (res.status === 401 && !retrying) {
    // One refresh attempt, then retry the original request once.
    const session = await refreshSession();
    if (session) return request<T>(path, { ...options, retrying: true });
    onSessionLost?.("Your session has ended. Please sign in again.");
    throw await toApiError(res);
  }

  if (!res.ok) throw await toApiError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: "POST", body }),
  patch: <T>(path: string, body?: unknown) => request<T>(path, { method: "PATCH", body }),
  delete: <T>(path: string) => request<T>(path, { method: "DELETE" }),
};
